import { useMemo } from "react";
import moment from "moment";
import {
  useGetAllCredentialsByOwner,
  useGetAllOfficeCredentialsByOwner,
} from "repositories/user-repository";
import constants, { CREDENTIAL_STATUS } from "../../../constants";
import { DOCUMENTATION_STATUS } from "../CredentialTypeStatus";

const PAGE_SIZE = constants.pageSize;

/**
 * Resolve DocumentationStatus for the credentials listing API.
 * All = 1, Proof Missing = 2, Completed = 3.
 * @param {number[]} documentationStatusApplied
 * @returns {number}
 */
const resolveDocumentStatus = (documentationStatusApplied) => {
  const selected = documentationStatusApplied || [];

  // All selected (or empty / both specific options) ⇒ DocumentationStatus: 1
  if (
    selected.length === 0 ||
    selected.some((id) => +id === DOCUMENTATION_STATUS.ALL)
  ) {
    return DOCUMENTATION_STATUS.ALL;
  }

  const specific = selected.filter((id) => +id !== DOCUMENTATION_STATUS.ALL);

  if (
    specific.some((id) => +id === DOCUMENTATION_STATUS.PROOF_MISSING) &&
    specific.some((id) => +id === DOCUMENTATION_STATUS.COMPLETED)
  ) {
    return DOCUMENTATION_STATUS.ALL;
  }

  if (specific.length === 1) {
    return specific[0];
  }

  return DOCUMENTATION_STATUS.ALL;
};

/**
 * @param {Object} queryResult - React Query result from credentials listing APIs
 * @returns {number}
 */
const getTotalItems = (queryResult) =>
  queryResult?.data?.pagination?.totalItems ??
  queryResult?.data?.totalItems ??
  0;

/**
 * Custom hook to fetch credentials data based on active tab and filters
 * @param {string} activeTab - Active tab ("1" for staff, "2" for office)
 * @param {string} selectedId - Owner ID
 * @param {Object} filters - Filter object from useCredentialsFilter
 * @returns {Object} Data, loading states, and refetch functions
 */
export const useCredentialsData = (
  activeTab,
  selectedId,
  filters,
  canAccessThisPage,
) => {
  const {
    startDateApplied,
    endDateApplied,
    selectedEmpFilterApplied,
    selectedOfficeFilterApplied,
    credentialTypesApplied,
    statusesApplied,
    sortBy,
    isDescending,
    searchValueApplied,
    radioCheckedApplied,
    documentationStatusApplied,
    pageNumber,
  } = filters;

  // Prepare API payload for staff credentials
  const staffPayload = useMemo(() => {
    const statuses = [...statusesApplied];
    if (
      radioCheckedApplied &&
      !statuses.some((id) => +id === CREDENTIAL_STATUS.EXPIRING_SOON)
    ) {
      statuses.push(CREDENTIAL_STATUS.EXPIRING_SOON);
    }
    return {
      ownerId: selectedId,
      startDate: startDateApplied
        ? moment(startDateApplied).format("YYYY-MM-DD")
        : null,
      endDate: endDateApplied
        ? moment(endDateApplied).format("YYYY-MM-DD")
        : null,
      userIds: selectedEmpFilterApplied,
      credentialTypes: credentialTypesApplied,
      statuses,
      sortBy: sortBy,
      isDescending: !isDescending,
      searchTerm: searchValueApplied,
      DocumentationStatus: resolveDocumentStatus(documentationStatusApplied),
      PageNumber: pageNumber,
      PageSize: PAGE_SIZE,
    };
  }, [
    selectedId,
    startDateApplied,
    endDateApplied,
    selectedEmpFilterApplied,
    credentialTypesApplied,
    statusesApplied,
    radioCheckedApplied,
    sortBy,
    isDescending,
    searchValueApplied,
    documentationStatusApplied,
    pageNumber,
  ]);

  // Prepare API payload for office credentials
  const officePayload = useMemo(() => {
    const statuses = [...statusesApplied];
    if (
      radioCheckedApplied &&
      !statuses.some((id) => +id === CREDENTIAL_STATUS.EXPIRING_SOON)
    ) {
      statuses.push(CREDENTIAL_STATUS.EXPIRING_SOON);
    }
    return {
      ownerId: selectedId,
      startDate: startDateApplied
        ? moment(startDateApplied).format("YYYY-MM-DD")
        : null,
      endDate: endDateApplied
        ? moment(endDateApplied).format("YYYY-MM-DD")
        : null,
      officeIds: selectedOfficeFilterApplied,
      credentialTypes: credentialTypesApplied,
      statuses: statuses,
      sortBy: sortBy,
      isDescending: !isDescending,
      searchTerm: searchValueApplied,
      DocumentationStatus: resolveDocumentStatus(documentationStatusApplied),
      PageNumber: pageNumber,
      PageSize: PAGE_SIZE,
    };
  }, [
    selectedId,
    startDateApplied,
    endDateApplied,
    selectedOfficeFilterApplied,
    credentialTypesApplied,
    statusesApplied,
    radioCheckedApplied,
    sortBy,
    isDescending,
    searchValueApplied,
    documentationStatusApplied,
    pageNumber,
  ]);

  // Fetch Staff Credentials (Tab 1)
  const staffCredentialsQuery = useGetAllCredentialsByOwner(staffPayload, {
    enabled: activeTab === "1" && !!selectedId && canAccessThisPage,
  });

  // Fetch Office Credentials (Tab 2)
  const officeCredentialsQuery = useGetAllOfficeCredentialsByOwner(
    officePayload,
    {
      enabled: activeTab === "2" && !!selectedId && canAccessThisPage,
    },
  );

  return {
    staffCredentials: {
      ...staffCredentialsQuery,
      ...(staffCredentialsQuery?.data || {}),
      data: staffCredentialsQuery?.data?.data || [],
      totalItems: getTotalItems(staffCredentialsQuery),
      isLoading:
        staffCredentialsQuery.isLoading || staffCredentialsQuery.isFetching,
      refetch: staffCredentialsQuery.refetch,
    },
    officeCredentials: {
      ...officeCredentialsQuery,
      ...(officeCredentialsQuery?.data || {}),
      data: officeCredentialsQuery?.data?.data || [],
      totalItems: getTotalItems(officeCredentialsQuery),
      isLoading:
        officeCredentialsQuery.isLoading || officeCredentialsQuery.isFetching,
      refetch: officeCredentialsQuery.refetch,
    },
  };
};
